import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { IngredientCategory } from "@/lib/types";
import { roundToNearest } from "@/lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { AdvancedForm } from "../advanced";
import { CreateIngredientFormData, NUTRITION_DERIVED_FIELDS } from "../schema";
import { getNutritionAdapter } from "@/lib/ingredients/nutrition/adapters";
import { NutrientFields } from "./nutrient-fields";
import { nutritionDraftSchema } from "./schema";
import { NutritionDerivationResult, NutritionDraft, WaterMode } from "./types";
type NutritionFormProps = {
  form: UseFormReturn<CreateIngredientFormData>;
  selectedCategory: IngredientCategory;
};
const EMPTY_DERIVATION: NutritionDerivationResult = {
  formPatch: {},
  estimatedWaterPct: 0,
  blockingIssues: [],
};
const INITIAL_DRAFT: NutritionDraft = {
  gramsPerServing: 100,
  caloriesPerServing: 0,
  fatPerServing: 0,
  saturatedFatPerServing: 0,
  transFatPerServing: 0,
  carbsPerServing: 0,
  sugarsPerServing: 0,
  fiberPerServing: 0,
  proteinPerServing: 0,
  sodiumMgPerServing: 0,
  addedSugarsPerServing: 0,
  waterMode: "estimated",
  waterGrams: 0,
};
export function NutritionForm({ form, selectedCategory }: NutritionFormProps) {
  const [draft, setDraft] = useState<NutritionDraft>(INITIAL_DRAFT);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const adapter = getNutritionAdapter(selectedCategory);
  useEffect(() => {
    setDraft(INITIAL_DRAFT);
  }, [selectedCategory]);
  const validationErrors = useMemo(() => {
    const parsed = nutritionDraftSchema.safeParse(draft);
    if (parsed.success) return {};
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0]?.toString();
      if (key && !fieldErrors[key]) {
        fieldErrors[key] = issue.message;
      }
    }
    return fieldErrors;
  }, [draft]);
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const result = useMemo(() => {
    if (!adapter) {
      return {
        ...EMPTY_DERIVATION,
        blockingIssues: [
          { title: "Nutrition mode is not available for this category." },
        ],
      };
    }
    if (hasValidationErrors) {
      return EMPTY_DERIVATION;
    }
    return adapter.derive(draft);
  }, [adapter, draft, hasValidationErrors]);
  const isBlocked = hasValidationErrors || result.blockingIssues.length > 0;
  useEffect(() => {
    if (isBlocked) {
      form.setError("root", {
        message: "Fix nutrition issues before submitting",
      });
    } else {
      form.clearErrors("root");
    }
  }, [isBlocked, form]);
  useEffect(() => {
    if (isBlocked) return;
    form.setValue("derivation", result.derivation);
    for (const field of NUTRITION_DERIVED_FIELDS) {
      const value = result.formPatch[field];
      if (value !== undefined) {
        form.setValue(field, value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
    return () => {
      // Estimates remain editable after leaving nutrition mode, but their
      // derivation must no longer recalculate or validate manual values.
      form.setValue("derivation", undefined);
    };
  }, [form, result, isBlocked]);
  const setDraftField = (field: keyof NutritionDraft, value: number) => {
    setDraft((prev) => ({
      ...prev,
      [field]: Number.isFinite(value) ? value : 0,
    }));
  };
  const setWaterMode = (mode: WaterMode) => {
    setDraft((prev) => ({
      ...prev,
      waterMode: mode,
      waterGrams: mode === "manual" ? estimatedWaterGrams : prev.waterGrams,
    }));
  };
  const includeAddedSugars = selectedCategory === "dairy";
  const estimatedWaterGrams = roundToNearest(
    (result.estimatedWaterPct / 100) * draft.gramsPerServing,
    0.1,
  );
  return (
    <div className="space-y-3">
      <NutrientFields
        draft={draft}
        onChange={setDraftField}
        onWaterModeChange={setWaterMode}
        includeAddedSugars={includeAddedSugars}
        estimatedWaterPct={result.estimatedWaterPct}
        estimatedWaterGrams={estimatedWaterGrams}
        errors={validationErrors}
      />
      {result.blockingIssues.map((issue, i) => (
        <Alert key={i} variant="destructive">
          <AlertTitle>{issue.title}</AlertTitle>
          {issue.description && (
            <AlertDescription>{issue.description}</AlertDescription>
          )}
        </Alert>
      ))}
      <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
        <CollapsibleTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground flex items-center pl-1"
            />
          }
        >
          {isAdvancedOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span>Advanced Properties</span>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-2">
          <AdvancedForm
            form={form}
            isDisabled
            selectedCategory={selectedCategory}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
